import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { DesktopAccountGate } from './AccountMenu';
import AriaOrbDemo from './AriaOrbDemo';
import { applyFirstRunPreview } from './firstRunPreview';
import './index.css';

// ?firstrun=new / ?firstrun=exit — replay the first-time onboarding as a
// stranger, or put your own account back. Runs BEFORE render on purpose: App
// reads localStorage in its state initializers, so a reset applied after the
// first paint would be a paint too late. No-op without the query param.
applyFirstRunPreview();

class AppErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(err) {
    return { hasError: true, error: err };
  }
  componentDidCatch(err, info) {
    console.error('Aria failed to load:', err, info);
  }
  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const stack = err?.stack ? String(err.stack).slice(0, 800) : '';
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui', color: '#fff', background: '#0f0f12', minHeight: '100vh', maxWidth: 720 }}>
          <h1 style={{ color: '#e55' }}>Something went wrong</h1>
          <p>{err?.message || 'Unknown error'}</p>
          {stack && <pre style={{ color: '#888', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{stack}</pre>}
          <p style={{ color: '#888', fontSize: 14 }}>Open the browser console (F12 → Console) for more details.</p>
          <button type="button" onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function showFatalError(container, title, message) {
  const html = `
    <div style="padding:24px;font-family:system-ui;color:#fff;background:#0f0f12;min-height:100vh">
      <h1 style="color:#e55">${title}</h1>
      <p>${(message || 'Unknown error').replace(/</g, '&lt;')}</p>
      <p style="color:#888;font-size:14px">Open the browser console (F12 → Console) for details.</p>
      <button type="button" onclick="window.location.reload()" style="margin-top:16px;padding:8px 16px;cursor:pointer">Reload</button>
    </div>
  `;
  if (container && container.innerHTML !== undefined) container.innerHTML = html;
  else document.body.innerHTML = html;
}

const root = document.getElementById('root');
if (!root) {
  showFatalError(document.body, 'Root element missing', 'Check the app setup.');
} else {
  try {
    // REUSE THE ROOT (8/22). Vite re-executes this module on every hot update,
    // and calling createRoot() again on a container that already has one makes
    // React warn and mount a second time — effects run twice, timers double up,
    // and the console fills with a warning that hides real errors. Caching the
    // root on the element means a hot update RE-RENDERS instead of re-mounting.
    // Harmless in a production build (this file runs once) and correct in dev,
    // which is where the app is actually driven.
    const reactRoot = root._ariaReactRoot || (root._ariaReactRoot = ReactDOM.createRoot(root));
    const isDemo = window.location.pathname === '/orb-demo' || window.location.hash === '#orb-demo';
    reactRoot.render(
      <AppErrorBoundary>
        {isDemo ? <AriaOrbDemo /> : <DesktopAccountGate><App /></DesktopAccountGate>}
      </AppErrorBoundary>
    );
  } catch (err) {
    console.error('Aria failed to start:', err);
    showFatalError(root, 'Failed to load', err?.message || String(err));
  }
}
